// PlanPreview — the editable plan shown BEFORE an autonomous run, pure.
//
// .claude/research/design/03: for a multi-step autonomous run the agent first shows a short,
// EDITABLE plan — "tighten 3 stems · add 1 worked example · re-check grounding"
// — that the author edits or approves BEFORE any content is generated. Reviewing
// intent (a few lines) is far cheaper than reviewing 2000 words after the fact.
// Approving the plan IS the consent for the run (the-rule-is-the-consent shape).
//
// Each row is a `PlanStep` variant; its leading glyph is a match over the kind
// (edit / add / verify), so the author reads at a glance which steps produce
// diffs (edit, add) and which are evaluation-in-the-loop (verify — re-check
// grounding, .claude/research/design/04). A new kind breaks the glyph match until handled.
//
// EDITING is expressed as reported intents, never internal state (pure): the
// step text is an Input driven by props, and typing reports `onStepChange`;
// removing a step reports `onRemoveStep`; approving reports `onApprove` and
// discarding `onDiscard`. The steps themselves live in the caller's state (story
// useState in the sketch), so the component holds nothing.
//
// Composes Input (the editable step text), IconButton (remove), Button (approve
// / discard), Text. Named-by-outcome actions: "Start" (not "run"), "Discard".

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../Button/Button.js";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import { Input } from "../../Input/Input.js";
import { Text } from "../../Text/Text.js";
import type { PlanStep, PlanStepId, PlanStepKind } from "../types.js";
import "./PlanPreview.css";

export type PlanPreviewProps = {
    readonly steps: readonly PlanStep[];
    // Editing intents — reported UP; the caller owns the steps.
    readonly onStepChange: (id: PlanStepId, description: string) => void;
    readonly onRemoveStep: (id: PlanStepId) => void;
    // Approving the plan IS the consent for the run.
    readonly onApprove: () => void;
    readonly onDiscard?: () => void;
    readonly className?: string;
};

const editGlyph = <Icon name="pencil" />;
const addGlyph = <Icon name="plus" />;
const verifyGlyph = <Icon name="shield" />;

// The leading glyph per step kind — a match, so a new kind fails to compile.
const stepGlyph = (kind: PlanStepKind): ReactNode => {
    switch (kind) {
        case "edit":
            return editGlyph;
        case "add":
            return addGlyph;
        case "verify":
            return verifyGlyph;
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

const removeGlyph = <Icon name="close" />;

export const PlanPreview = ({
    steps,
    onStepChange,
    onRemoveStep,
    onApprove,
    onDiscard,
    className,
}: PlanPreviewProps) => {
    const { t } = useLingui();
    return (
        <section
            className={`bilda-plan-preview${className ? ` ${className}` : ""}`}
            aria-label={t`Plan for the automatic run`}
        >
            <Text
                className="bilda-plan-preview__title"
                size="sm"
                weight="semibold"
                as="p"
            >
                <Trans>Plan — approve before the agent runs</Trans>
            </Text>

            <ul className="bilda-plan-preview__steps">
                {steps.map((step) => (
                    <li key={step.id} className="bilda-plan-preview__step">
                        <span
                            className="bilda-plan-preview__glyph"
                            aria-hidden="true"
                        >
                            {stepGlyph(step.type)}
                        </span>
                        <Input
                            className="bilda-plan-preview__input"
                            size="sm"
                            value={step.description}
                            aria-label={t`Plan step`}
                            onChange={(event) =>
                                onStepChange(step.id, event.target.value)
                            }
                        />
                        <IconButton
                            label={t`Remove step`}
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveStep(step.id)}
                        >
                            {removeGlyph}
                        </IconButton>
                    </li>
                ))}
            </ul>

            <div className="bilda-plan-preview__actions">
                {onDiscard ? (
                    <Button variant="ghost" size="sm" onClick={onDiscard}>
                        <Trans>Discard</Trans>
                    </Button>
                ) : null}
                <Button
                    variant="primary"
                    size="sm"
                    onClick={onApprove}
                    disabled={steps.length === 0}
                >
                    <Trans>Start</Trans>
                </Button>
            </div>
        </section>
    );
};
